import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import { ChevronDown, LayersThree01 } from '@opendate-app/setlist';
import ConductorResourceCards, { resourceCardPropType } from './ConductorResourceCards';
import ToolCallIndicator from './ToolCallIndicator';
import { contextPercentage } from '../threadActivity';
import { conductorTheme } from '../themeTokens';

const popupContentStyle = {
  border: 'none',
  padding: 0,
  background: 'transparent',
  boxShadow: 'none',
  width: 'auto'
};

const CONTEXT_TRIGGER_THRESHOLD_PERCENTAGE = 60;

const ACTIVITY_STYLES = `
  @keyframes venue-conductor-panel-in {
    from { opacity: 0; transform: scale(0.97); }
    to { opacity: 1; transform: scale(1); }
  }
  .venue-conductor-activity-panel { animation: venue-conductor-panel-in 180ms cubic-bezier(0.23, 1, 0.32, 1); }
  @keyframes venue-conductor-count-pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.15); }
    100% { transform: scale(1); }
  }
  .venue-conductor-activity-count-pulse { animation: venue-conductor-count-pulse 200ms cubic-bezier(0.23, 1, 0.32, 1); }
  @media (prefers-reduced-motion: reduce) {
    .venue-conductor-activity-panel,
    .venue-conductor-activity-count-pulse { animation: none; }
  }
`;

const formatTokenCount = (value) => {
  if (!value) return '0';
  if (value >= 1000000) return `${(value / 1000000).toFixed(value >= 10000000 ? 0 : 1)}m`;
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;

  return value.toLocaleString();
};

const formatPercentage = (value) => {
  if (value == null) return '—';
  if (value > 0 && value < 1) return '<1%';

  return `${Math.round(value)}%`;
};

const ConductorThreadActivity = ({
  activeToolCalls,
  completedToolCalls,
  resourceCards,
  referencedCards,
  onOpenManualArticle,
  panelAlignment,
  usage,
  contextUsage,
  condensed
}) => {
  const [selectedTab, setSelectedTab] = useState('tools');
  const percentage = contextUsage?.percentage != null ? contextUsage.percentage : contextPercentage(usage);
  const urgentContext = contextUsage?.threshold_state === 'urgent' || (percentage != null && percentage >= 80);
  const hasActivity = activeToolCalls.length > 0 || completedToolCalls.length > 0 ||
    resourceCards.length > 0 || referencedCards.length > 0;
  const toolCount = activeToolCalls.length + completedToolCalls.length;
  const totalItems = toolCount + resourceCards.length + referencedCards.length;

  // Items landing while the popup is closed accumulate as unseen; a thread
  // switch (total drops) resets the baseline so history never counts as unread.
  const seenItemsRef = useRef(null);
  const [unseenCount, setUnseenCount] = useState(0);
  useEffect(() => {
    if (seenItemsRef.current == null || totalItems < seenItemsRef.current) {
      seenItemsRef.current = totalItems;
      setUnseenCount(0);
      return;
    }
    setUnseenCount(totalItems - seenItemsRef.current);
  }, [totalItems]);

  const markSeen = () => {
    seenItemsRef.current = totalItems;
    setUnseenCount(0);
  };

  const selectTab = (tab) => () => {
    setSelectedTab(tab);
  };

  const activityTrigger = (open) => (
    <button
      type="button"
      aria-label="Show thread activity"
      aria-haspopup="true"
      aria-expanded={open}
      title="Tools, created records, and context for this thread"
      className="venue-conductor-btn"
      onClickCapture={selectTab('tools')}
      style={{
        height: '36px',
        minWidth: 0,
        borderRadius: '999px',
        border: `1px solid ${open ? conductorTheme.accentBorder : conductorTheme.border}`,
        backgroundColor: open ? conductorTheme.accentSurface : conductorTheme.surfaceSoft,
        color: conductorTheme.textSlate,
        padding: '0 11px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap'
      }}
    >
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <LayersThree01 width={16} height={16} aria-hidden="true" />
        {(activeToolCalls.length > 0 || unseenCount > 0) && (
          <span style={{
            position: 'absolute',
            top: '-4px',
            right: '-5px',
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            backgroundColor: conductorTheme.brandCoral,
            boxShadow: `0 0 0 2px ${conductorTheme.surfaceSoft}`
          }} />
        )}
      </span>
      Activity
      {hasActivity && (
        <span
          key={totalItems}
          className={unseenCount > 0 ? 'venue-conductor-activity-count-pulse' : undefined}
          style={{ color: conductorTheme.textMuted, fontVariantNumeric: 'tabular-nums' }}
        >
          {totalItems}
        </span>
      )}
    </button>
  );

  const contextTrigger = (open) => (
    <button
      type="button"
      aria-label="Show context usage"
      aria-haspopup="true"
      aria-expanded={open}
      title="Context window usage for this thread"
      className="venue-conductor-btn"
      onClickCapture={selectTab('context')}
      style={{
        height: '36px',
        minWidth: '190px',
        borderRadius: '999px',
        border: `1px solid ${open ? conductorTheme.accentBorder : conductorTheme.border}`,
        backgroundColor: open ? conductorTheme.accentSurface : conductorTheme.surfaceSoft,
        color: conductorTheme.textSlate,
        padding: '0 11px 0 13px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '9px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 700,
        whiteSpace: 'nowrap'
      }}
    >
      <span>Context</span>
      <span
        role="progressbar"
        aria-label="Context used"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={percentage == null ? undefined : Math.round(percentage)}
        style={{
          width: '64px',
          height: '5px',
          borderRadius: '999px',
          backgroundColor: conductorTheme.surfaceMuted,
          overflow: 'hidden'
        }}
      >
        <span style={{
          display: 'block',
          height: '100%',
          width: `${percentage || 0}%`,
          minWidth: percentage > 0 ? '3px' : 0,
          borderRadius: '999px',
          background: urgentContext
            ? conductorTheme.brandCoralGradient
            : `linear-gradient(90deg, ${conductorTheme.accentText}, #38bdf8)`
        }} />
      </span>
      <span style={{ minWidth: '27px', textAlign: 'right', color: conductorTheme.textMuted, fontVariantNumeric: 'tabular-nums' }}>
        {formatPercentage(percentage)}
      </span>
      <ChevronDown width={10} height={10} aria-hidden="true" style={{ color: conductorTheme.iconMuted }} />
    </button>
  );

  const renderTools = () => (
    <div>
      {activeToolCalls.length === 0 && completedToolCalls.length === 0 ? (
        <div style={{ color: conductorTheme.textMuted, fontSize: '13px', padding: '16px 4px' }}>
          Tool activity will collect here as Conductor works.
        </div>
      ) : (
        <>
          {activeToolCalls.map((toolCall, index) => (
            <ToolCallIndicator key={`active-${toolCall.id || toolCall.tool_name}-${index}`} toolCall={toolCall} isActive={true} />
          ))}
          {completedToolCalls.map((toolCall, index) => (
            <ToolCallIndicator key={`completed-${toolCall.id || toolCall.tool_name}-${index}`} toolCall={toolCall} isActive={false} />
          ))}
        </>
      )}
    </div>
  );

  const renderCreated = () => resourceCards.length > 0 ? (
    <ConductorResourceCards cards={resourceCards} onOpenManualArticle={onOpenManualArticle} />
  ) : (
    <div style={{ color: conductorTheme.textMuted, fontSize: '13px', padding: '16px 4px' }}>
      Records and exports created in this thread will collect here.
    </div>
  );

  const renderReferenced = () => referencedCards.length > 0 ? (
    <ConductorResourceCards cards={referencedCards} onOpenManualArticle={onOpenManualArticle} />
  ) : (
    <div style={{ color: conductorTheme.textMuted, fontSize: '13px', padding: '16px 4px' }}>
      Records Conductor looks up in this thread will collect here.
    </div>
  );

  const renderContext = () => (
    <div style={{ padding: '6px 2px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '28px', lineHeight: 1.1, fontWeight: 700, color: conductorTheme.textStrong, fontVariantNumeric: 'tabular-nums' }}>
            {formatPercentage(percentage)}
          </div>
          <div style={{ marginTop: '4px', fontSize: '12px', color: conductorTheme.textMuted }}>
            {contextUsage ? 'Thread context' : 'Last request context'}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '12px', color: conductorTheme.textMuted, lineHeight: 1.5 }}>
          {contextUsage ? (
            <div>{formatTokenCount(contextUsage.estimated_tokens)} of {formatTokenCount(contextUsage.usable_window)} tokens</div>
          ) : (
            <div>{formatTokenCount(usage?.last_context_tokens)} of {formatTokenCount(usage?.last_context_window_tokens)} tokens</div>
          )}
          <div>{formatTokenCount(usage?.total_tokens)} processed across {usage?.request_count || 0} requests</div>
        </div>
      </div>
      <div style={{ height: '7px', marginTop: '16px', borderRadius: '999px', backgroundColor: conductorTheme.surfaceMuted, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${percentage || 0}%`,
          minWidth: percentage > 0 ? '4px' : 0,
          borderRadius: '999px',
          background: urgentContext
            ? conductorTheme.brandCoralGradient
            : `linear-gradient(90deg, ${conductorTheme.accentText}, #38bdf8)`
        }} />
      </div>
      {contextUsage?.compacted && (
        <div style={{ marginTop: '12px', color: conductorTheme.textMuted, fontSize: '13px' }}>
          Earlier turns are compacted into a summary.
        </div>
      )}
      {!contextUsage && !usage?.last_context_window_tokens && (
        <div style={{ marginTop: '12px', color: conductorTheme.textMuted, fontSize: '13px' }}>
          Context usage will appear after Conductor completes a measured request.
        </div>
      )}
      {(contextUsage?.model_id || usage?.last_model_id) && (
        <div style={{ marginTop: '10px', color: conductorTheme.textSubtle, fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {contextUsage?.model_id || usage.last_model_id}
        </div>
      )}
    </div>
  );

  const renderPanel = (transformOrigin) => (
    <div className="venue-conductor-activity-panel" style={{
      width: condensed ? 'min(380px, calc(var(--conductor-frame-width, 100vw) - 24px))' : 'min(520px, calc(100vw - 32px))',
      maxHeight: condensed ? 'min(62vh, 560px)' : 'min(58vh, 620px)',
      transformOrigin,
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${conductorTheme.borderBlue}`,
      borderRadius: '14px',
      backgroundColor: conductorTheme.surface,
      boxShadow: '0 22px 55px rgba(15, 23, 42, 0.18)',
      overflow: 'hidden'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px', borderBottom: `1px solid ${conductorTheme.border}` }}>
        {[
          { id: 'tools', label: `Tools ${toolCount}` },
          { id: 'created', label: `Created ${resourceCards.length}` },
          { id: 'referenced', label: `Referenced ${referencedCards.length}` },
          { id: 'context', label: 'Context' }
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSelectedTab(tab.id)}
            style={{
              flex: 1,
              minWidth: 0,
              whiteSpace: 'nowrap',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: selectedTab === tab.id ? conductorTheme.accentSurface : 'transparent',
              color: selectedTab === tab.id ? conductorTheme.accentText : conductorTheme.textMuted,
              padding: '7px 8px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div style={{ overflowY: 'auto', padding: '12px' }}>
        {selectedTab === 'tools' && renderTools()}
        {selectedTab === 'created' && renderCreated()}
        {selectedTab === 'referenced' && renderReferenced()}
        {selectedTab === 'context' && renderContext()}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: condensed ? 'auto' : '100%' }}>
      <style>{ACTIVITY_STYLES}</style>
      <Popup
        on="click"
        arrow={false}
        offsetY={6}
        position={panelAlignment === 'right' ? 'bottom right' : 'bottom left'}
        closeOnDocumentClick
        contentStyle={popupContentStyle}
        trigger={activityTrigger}
        onOpen={markSeen}
        onClose={markSeen}
      >
        {renderPanel(panelAlignment === 'right' ? 'top right' : 'top left')}
      </Popup>
      {!condensed && percentage != null && percentage >= CONTEXT_TRIGGER_THRESHOLD_PERCENTAGE && (
        <Popup
          on="click"
          arrow={false}
          offsetY={6}
          position="bottom right"
          closeOnDocumentClick
          contentStyle={popupContentStyle}
          trigger={contextTrigger}
          onOpen={markSeen}
          onClose={markSeen}
        >
          {renderPanel('top right')}
        </Popup>
      )}
    </div>
  );
};

const toolCallPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tool_name: PropTypes.string.isRequired,
  display_name: PropTypes.string.isRequired,
  success: PropTypes.bool,
  summary: PropTypes.string
});

ConductorThreadActivity.propTypes = {
  activeToolCalls: PropTypes.arrayOf(toolCallPropType),
  completedToolCalls: PropTypes.arrayOf(toolCallPropType),
  resourceCards: PropTypes.arrayOf(resourceCardPropType),
  referencedCards: PropTypes.arrayOf(resourceCardPropType),
  onOpenManualArticle: PropTypes.func,
  panelAlignment: PropTypes.oneOf(['left', 'right']),
  usage: PropTypes.shape({
    request_count: PropTypes.number,
    total_tokens: PropTypes.number,
    last_context_tokens: PropTypes.number,
    last_context_window_tokens: PropTypes.number,
    last_model_id: PropTypes.string
  }),
  contextUsage: PropTypes.shape({
    estimated_tokens: PropTypes.number,
    usable_window: PropTypes.number,
    percentage: PropTypes.number,
    threshold_state: PropTypes.string,
    compacted: PropTypes.bool,
    model_id: PropTypes.string
  }),
  condensed: PropTypes.bool
};

ConductorThreadActivity.defaultProps = {
  activeToolCalls: [],
  completedToolCalls: [],
  resourceCards: [],
  referencedCards: [],
  onOpenManualArticle: null,
  panelAlignment: 'left',
  usage: null,
  contextUsage: null,
  condensed: false
};

export default ConductorThreadActivity;
